// WGS84 geodesics, ported from the Android app's Geodesy.kt (iOS runs the same maths). Every figure
// the measure tool shows — distance, bearing, ticks, the lock circle and ray, range placement —
// comes from here, so the web, iOS and Android agree to the millimetre.

/** A WGS84 position in decimal degrees. Shaped like mapboxgl.LngLat, so one can be passed as is. */
export interface GeoPoint {
  lat: number;
  lng: number;
}

/** The inverse problem: how far apart two points are and which way the first faces the second. */
export interface GeodesicInverse {
  /** Metres along the ellipsoid. */
  distance: number;
  /** True bearing at the first point, [0, 360). */
  initialBearing: number;
  /** True bearing of travel on arrival at the second point, [0, 360). */
  finalBearing: number;
}

/** The direct problem: where a bearing and a distance land, and the heading on arrival. */
export interface GeodesicDestination {
  point: GeoPoint;
  finalBearing: number;
}

const A = 6_378_137.0;
const F = 1 / 298.257223563;
const B = A * (1 - F);
const MEAN_RADIUS = 6_371_008.8;
const EPSILON = 1e-12;
const MAX_ITERATIONS = 200;
const VINCENTY_ABOVE_METERS = 10_000.0;

// The same doubles as Java's Math.toRadians / toDegrees.
const DEG_TO_RAD = Math.PI / 180;
const RAD_TO_DEG = 180 / Math.PI;

export const toRadians = (degrees: number): number => degrees * DEG_TO_RAD;
export const toDegrees = (radians: number): number => radians * RAD_TO_DEG;

const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max);

export function normalizeBearing(degrees: number): number {
  const wrapped = degrees % 360.0;
  const positive = wrapped < 0 ? wrapped + 360.0 : wrapped;
  // -1e-15 % 360 + 360 rounds to 360.0, which is not in [0, 360).
  return positive >= 360.0 ? 0.0 : positive;
}

/** A longitude in [-180, 180); one already in range is returned exactly as it is. */
export function normalizeLongitude(longitude: number): number {
  if (longitude >= -180.0 && longitude < 180.0) return longitude;
  let lon = (longitude + 180.0) % 360.0;
  if (lon < 0) lon += 360.0;
  return lon - 180.0;
}

/** A longitude difference folded into [-180, 180). */
export function wrapDelta(delta: number): number {
  return normalizeLongitude(delta);
}

const ZERO_INVERSE: GeodesicInverse = { distance: 0, initialBearing: 0, finalBearing: 0 };

function vincentyInverse(from: GeoPoint, to: GeoPoint): GeodesicInverse | null {
  // Wrapped, so a pair either side of the antimeridian is 0.2° apart rather than 359.8°.
  const l = toRadians(normalizeLongitude(to.lng - from.lng));
  const u1 = Math.atan((1 - F) * Math.tan(toRadians(from.lat)));
  const u2 = Math.atan((1 - F) * Math.tan(toRadians(to.lat)));
  const sinU1 = Math.sin(u1);
  const cosU1 = Math.cos(u1);
  const sinU2 = Math.sin(u2);
  const cosU2 = Math.cos(u2);

  let lambda = l;
  let previous: number;
  let iterations = 0;
  let sinLambda: number;
  let cosLambda: number;
  let sinSigma: number;
  let cosSigma: number;
  let sigma: number;
  let sinAlpha: number;
  let cosSqAlpha: number;
  let cos2SigmaM: number;
  do {
    sinLambda = Math.sin(lambda);
    cosLambda = Math.cos(lambda);
    const t1 = cosU2 * sinLambda;
    const t2 = cosU1 * sinU2 - sinU1 * cosU2 * cosLambda;
    sinSigma = Math.sqrt(t1 * t1 + t2 * t2);
    if (sinSigma === 0) return { ...ZERO_INVERSE };
    cosSigma = sinU1 * sinU2 + cosU1 * cosU2 * cosLambda;
    sigma = Math.atan2(sinSigma, cosSigma);
    sinAlpha = cosU1 * cosU2 * sinLambda / sinSigma;
    cosSqAlpha = 1 - sinAlpha * sinAlpha;
    cos2SigmaM = cosSqAlpha !== 0 ? cosSigma - 2 * sinU1 * sinU2 / cosSqAlpha : 0;
    const c = F / 16 * cosSqAlpha * (4 + F * (4 - 3 * cosSqAlpha));
    previous = lambda;
    lambda = l + (1 - c) * F * sinAlpha *
      (sigma + c * sinSigma * (cos2SigmaM + c * cosSigma * (-1 + 2 * cos2SigmaM * cos2SigmaM)));
    if (Math.abs(lambda) > Math.PI) return null;
    iterations++;
  } while (Math.abs(lambda - previous) > EPSILON && iterations < MAX_ITERATIONS);
  if (iterations >= MAX_ITERATIONS) return null;

  // Everything below must come from the converged λ, not the iterate before it: on a line of a
  // metre or two sin λ is ~1e-7, so even a 1e-12 step skews the azimuth by ~1e-4°.
  sinLambda = Math.sin(lambda);
  cosLambda = Math.cos(lambda);
  const t1 = cosU2 * sinLambda;
  const t2 = cosU1 * sinU2 - sinU1 * cosU2 * cosLambda;
  sinSigma = Math.sqrt(t1 * t1 + t2 * t2);
  if (sinSigma === 0) return { ...ZERO_INVERSE };
  cosSigma = sinU1 * sinU2 + cosU1 * cosU2 * cosLambda;
  sigma = Math.atan2(sinSigma, cosSigma);
  sinAlpha = cosU1 * cosU2 * sinLambda / sinSigma;
  cosSqAlpha = 1 - sinAlpha * sinAlpha;
  cos2SigmaM = cosSqAlpha !== 0 ? cosSigma - 2 * sinU1 * sinU2 / cosSqAlpha : 0;

  const uSq = cosSqAlpha * (A * A - B * B) / (B * B);
  const bigA = 1 + uSq / 16384 * (4096 + uSq * (-768 + uSq * (320 - 175 * uSq)));
  const bigB = uSq / 1024 * (256 + uSq * (-128 + uSq * (74 - 47 * uSq)));
  const deltaSigma = bigB * sinSigma * (cos2SigmaM + bigB / 4 * (cosSigma * (-1 + 2 * cos2SigmaM * cos2SigmaM) -
    bigB / 6 * cos2SigmaM * (-3 + 4 * sinSigma * sinSigma) * (-3 + 4 * cos2SigmaM * cos2SigmaM)));
  const distance = B * bigA * (sigma - deltaSigma);
  const alpha1 = Math.atan2(cosU2 * sinLambda, cosU1 * sinU2 - sinU1 * cosU2 * cosLambda);
  const alpha2 = Math.atan2(cosU1 * sinLambda, -sinU1 * cosU2 + cosU1 * sinU2 * cosLambda);
  return {
    distance,
    initialBearing: normalizeBearing(toDegrees(alpha1)),
    finalBearing: normalizeBearing(toDegrees(alpha2)),
  };
}

function vincentyDirect(from: GeoPoint, bearing: number, distance: number): GeodesicDestination | null {
  const alpha1 = toRadians(bearing);
  const sinAlpha1 = Math.sin(alpha1);
  const cosAlpha1 = Math.cos(alpha1);
  const tanU1 = (1 - F) * Math.tan(toRadians(from.lat));
  const cosU1 = 1 / Math.sqrt(1 + tanU1 * tanU1);
  const sinU1 = tanU1 * cosU1;
  const sigma1 = Math.atan2(tanU1, cosAlpha1);
  const sinAlpha = cosU1 * sinAlpha1;
  const cosSqAlpha = 1 - sinAlpha * sinAlpha;
  const uSq = cosSqAlpha * (A * A - B * B) / (B * B);
  const bigA = 1 + uSq / 16384 * (4096 + uSq * (-768 + uSq * (320 - 175 * uSq)));
  const bigB = uSq / 1024 * (256 + uSq * (-128 + uSq * (74 - 47 * uSq)));

  let sigma = distance / (B * bigA);
  let previous: number;
  let iterations = 0;
  let sinSigma: number;
  let cosSigma: number;
  let cos2SigmaM: number;
  do {
    cos2SigmaM = Math.cos(2 * sigma1 + sigma);
    sinSigma = Math.sin(sigma);
    cosSigma = Math.cos(sigma);
    const deltaSigma = bigB * sinSigma * (cos2SigmaM + bigB / 4 * (cosSigma * (-1 + 2 * cos2SigmaM * cos2SigmaM) -
      bigB / 6 * cos2SigmaM * (-3 + 4 * sinSigma * sinSigma) * (-3 + 4 * cos2SigmaM * cos2SigmaM)));
    previous = sigma;
    sigma = distance / (B * bigA) + deltaSigma;
    iterations++;
  } while (Math.abs(sigma - previous) > EPSILON && iterations < MAX_ITERATIONS);
  if (iterations >= MAX_ITERATIONS) return null;

  sinSigma = Math.sin(sigma);
  cosSigma = Math.cos(sigma);
  cos2SigmaM = Math.cos(2 * sigma1 + sigma);
  const x = sinU1 * sinSigma - cosU1 * cosSigma * cosAlpha1;
  const phi2 = Math.atan2(sinU1 * cosSigma + cosU1 * sinSigma * cosAlpha1, (1 - F) * Math.sqrt(sinAlpha * sinAlpha + x * x));
  const lambda = Math.atan2(sinSigma * sinAlpha1, cosU1 * cosSigma - sinU1 * sinSigma * cosAlpha1);
  const c = F / 16 * cosSqAlpha * (4 + F * (4 - 3 * cosSqAlpha));
  const bigL = lambda - (1 - c) * F * sinAlpha *
    (sigma + c * sinSigma * (cos2SigmaM + c * cosSigma * (-1 + 2 * cos2SigmaM * cos2SigmaM)));
  const alpha2 = Math.atan2(sinAlpha, -x);
  return {
    point: { lat: toDegrees(phi2), lng: normalizeLongitude(from.lng + toDegrees(bigL)) },
    finalBearing: normalizeBearing(toDegrees(alpha2)),
  };
}

/** The spherical fallback for the near-antipodal pairs where Vincenty's iteration fails. */
export function sphericalInverse(from: GeoPoint, to: GeoPoint): GeodesicInverse {
  const phi1 = toRadians(from.lat);
  const phi2 = toRadians(to.lat);
  const dPhi = phi2 - phi1;
  const dLambda = toRadians(to.lng - from.lng);
  const h = Math.sin(dPhi / 2) * Math.sin(dPhi / 2) +
    Math.cos(phi1) * Math.cos(phi2) * Math.sin(dLambda / 2) * Math.sin(dLambda / 2);
  const distance = 2 * MEAN_RADIUS * Math.asin(Math.sqrt(clamp(h, 0.0, 1.0)));
  const initial = Math.atan2(
    Math.sin(dLambda) * Math.cos(phi2),
    Math.cos(phi1) * Math.sin(phi2) - Math.sin(phi1) * Math.cos(phi2) * Math.cos(dLambda),
  );
  const back = Math.atan2(
    Math.sin(-dLambda) * Math.cos(phi1),
    Math.cos(phi2) * Math.sin(phi1) - Math.sin(phi2) * Math.cos(phi1) * Math.cos(-dLambda),
  );
  return {
    distance,
    initialBearing: normalizeBearing(toDegrees(initial)),
    finalBearing: normalizeBearing(toDegrees(back) + 180.0),
  };
}

export function sphericalDirect(from: GeoPoint, bearing: number, distance: number): GeodesicDestination {
  const delta = distance / MEAN_RADIUS;
  const theta = toRadians(bearing);
  const phi1 = toRadians(from.lat);
  const lambda1 = toRadians(from.lng);
  const phi2 = Math.asin(Math.sin(phi1) * Math.cos(delta) + Math.cos(phi1) * Math.sin(delta) * Math.cos(theta));
  const lambda2 = lambda1 + Math.atan2(
    Math.sin(theta) * Math.sin(delta) * Math.cos(phi1),
    Math.cos(delta) - Math.sin(phi1) * Math.sin(phi2),
  );
  const point = { lat: toDegrees(phi2), lng: normalizeLongitude(toDegrees(lambda2)) };
  const back = sphericalInverse(point, from).initialBearing;
  return { point, finalBearing: normalizeBearing(back + 180.0) };
}

/** Vincenty's inverse, with a spherical fallback where it does not converge. */
export function inverse(from: GeoPoint, to: GeoPoint): GeodesicInverse {
  if (from.lat === to.lat && from.lng === to.lng) return { ...ZERO_INVERSE };
  return vincentyInverse(from, to) ?? sphericalInverse(from, to);
}

/** Vincenty's direct problem; a zero distance returns [from] itself. */
export function destination(from: GeoPoint, bearing: number, distance: number): GeodesicDestination {
  if (distance === 0) return { point: from, finalBearing: normalizeBearing(bearing) };
  return vincentyDirect(from, bearing, distance) ?? sphericalDirect(from, bearing, distance);
}

/** Metres between two points along the ellipsoid (Android `Geodesy.distance`). */
export function geodesicDistance(from: GeoPoint, to: GeoPoint): number {
  return inverse(from, to).distance;
}

/** True bearing from [from] towards [to], [0, 360). */
export function initialBearing(from: GeoPoint, to: GeoPoint): number {
  return inverse(from, to).initialBearing;
}

/**
 * Ellipsoidal distance for the short hops between a route's vertices: the local radii of curvature
 * at the mid-latitude, within a millimetre of Vincenty over a kilometre at a fraction of the cost.
 * Hops longer than 10 km go through Vincenty.
 */
export function segmentDistance(from: GeoPoint, to: GeoPoint): number {
  const midLat = toRadians((from.lat + to.lat) / 2);
  const sinMid = Math.sin(midLat);
  const eSq = F * (2 - F);
  const w = 1 - eSq * sinMid * sinMid;
  const meridional = A * (1 - eSq) / (w * Math.sqrt(w));
  const primeVertical = A / Math.sqrt(w);
  const dPhi = toRadians(to.lat - from.lat);
  const dLambda = toRadians(normalizeLongitude(to.lng - from.lng));
  const north = meridional * dPhi;
  const east = primeVertical * Math.cos(midLat) * dLambda;
  const approximate = Math.sqrt(north * north + east * east);
  return approximate > VINCENTY_ABOVE_METERS ? geodesicDistance(from, to) : approximate;
}

/**
 * Points along the geodesic [from] → [to], both ends included, no two more than [maxSegmentMeters]
 * apart and never more than [maxSegments] segments — so a long line drawn on a Mercator map bends
 * the way the real shortest path does.
 */
export function densify(from: GeoPoint, to: GeoPoint, maxSegmentMeters = 1_000.0, maxSegments = 64): GeoPoint[] {
  const line = inverse(from, to);
  if (line.distance === 0) return [from, to];
  const segments = clamp(Math.ceil(line.distance / maxSegmentMeters), 1, maxSegments);
  const points: GeoPoint[] = [from];
  for (let i = 1; i < segments; i++) {
    points.push(destination(from, line.initialBearing, line.distance * i / segments).point);
  }
  points.push(to);
  return points;
}

/**
 * The same points with each longitude moved by whole turns to within 180° of the one before, so a
 * line or ring across the antimeridian stays continuous (179.9, 180.1 …) instead of jumping a whole
 * world width. For drawing; Mapbox renders longitudes beyond ±180 on the wrapped copy.
 */
export function unwrapLongitudes(points: GeoPoint[]): GeoPoint[] {
  if (points.length < 2) return points;
  const result: GeoPoint[] = [points[0]];
  let previous = points[0].lng;
  for (let i = 1; i < points.length; i++) {
    // Whole turns only, and only across a jump: a point already within 180° of the one before is
    // passed through untouched (no add-then-subtract rounding).
    let longitude = points[i].lng;
    while (longitude - previous > 180.0) longitude -= 360.0;
    while (longitude - previous < -180.0) longitude += 360.0;
    result.push(longitude === points[i].lng ? points[i] : { lat: points[i].lat, lng: longitude });
    previous = longitude;
  }
  return result;
}
